import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import actions from './photos.actions';
import photosAdapter from './photos.adapter';

import type { PhotosSliceInitialState, PhotoUpload, CollectionName } from './photos.types';

const initialState: PhotosSliceInitialState = {
  status: 'idle',
  activeCollection: null,
  newUploadsCollection: '',
};

const slice = createSlice({
  name: 'photos',
  initialState: photosAdapter.getInitialState(initialState),
  reducers: {
    reset: () => photosAdapter.getInitialState(initialState),
    // addPhotos/addMany are not functionally different, just not ready to refactor addPhotos right now
    addPhotos(state, { payload }: PayloadAction<PhotoUpload[]>) {
      return photosAdapter.addMany(state, payload);
    },
    addMany: photosAdapter.addMany,
    updateOne: photosAdapter.updateOne,
    updateMany: photosAdapter.updateMany,
    setActiveCollection(state, { payload }: PayloadAction<CollectionName>) {
      return { ...state, activeCollection: payload };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.getAll.fulfilled, (state, { payload }) => {
        return photosAdapter.addMany(state, payload);
      })
      .addCase(actions.update.fulfilled, (state, { payload }) => {
        return photosAdapter.updateOne(state, { id: payload.id, changes: payload });
      })
      .addCase(actions.update.rejected, (state) => {
        return { ...state, status: 'error' };
      })
      .addCase(actions.removePhoto.fulfilled, (state, { payload }) => {
        const update = photosAdapter.removeOne(state, payload.id);
        update.status = 'idle';
        return update;
      })
      .addCase(actions.removeManyPhotos.fulfilled, (state, { payload }) => {
        const update = photosAdapter.removeMany(state, payload);
        update.status = 'idle';
        return update;
      })
      .addCase(actions.uploadPhotos.fulfilled, (state, { payload }) => {
        const update = photosAdapter.addMany(state, payload);
        update.status = 'idle';

        if (payload[0].collection) {
          update.activeCollection = payload[0].collection;
        }

        return update;
      })
      .addCase(actions.renameCollectionPhotos.fulfilled, (state, { payload }) => {
        const changes = payload.map(({ id, collection }) => ({ id, changes: { collection } }));
        const update = photosAdapter.updateMany(state, changes);
        update.activeCollection = payload[0].collection;
        return update;
      })
      .addCase(actions.createNewCollection, (state, { payload }) => {
        if (payload.action === 'create_from_upload') {
          return { ...state, newUploadsCollection: payload.name };
        }
      });
  },
});

export default slice;
